// Read-only public data access and the trusted signup request for Comp of the
// Week. Scoring, map selection, replay validation, and all event writes belong
// to services/cotw-worker; this module never writes a score from the browser.

import {
  collection,
  documentId,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase/config';
import {
  COTW_PHASES,
  DEFAULT_COTW_CONFIG,
  cotwEventKeyFor,
  nextCotwStart,
} from '../lib/compOfTheWeek';

export const COTW_CONFIG_COLLECTION = 'cotwConfig';
export const COTW_CONFIG_DOC = 'settings';
export const COTW_EVENTS_COLLECTION = 'cotwEvents';
export const COTW_STEAM_CLAIMS_COLLECTION = 'cotwSteamClaims';

const boundedNumber = (
  value,
  fallback,
  { min = 0, max = Number.POSITIVE_INFINITY, integer = false } = {}
) => {
  const number = Number(value);
  return Number.isFinite(number) &&
    number >= min &&
    number <= max &&
    (!integer || Number.isInteger(number))
    ? number
    : fallback;
};

const validTimeZone = (value) => {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
};

const withConfigDefaults = (data = {}) => {
  const source = data && typeof data === 'object' ? data : {};
  return {
    ...DEFAULT_COTW_CONFIG,
    ...source,
    enabled: source.enabled !== false,
    timeZone: validTimeZone(source.timeZone) ? source.timeZone : DEFAULT_COTW_CONFIG.timeZone,
    startTime:
      typeof source.startTime === 'string' && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(source.startTime)
        ? source.startTime
        : DEFAULT_COTW_CONFIG.startTime,
    dayOfWeek: boundedNumber(source.dayOfWeek, DEFAULT_COTW_CONFIG.dayOfWeek, {
      min: 0,
      max: 6,
      integer: true,
    }),
    qualifierMinutes: boundedNumber(source.qualifierMinutes, DEFAULT_COTW_CONFIG.qualifierMinutes, {
      min: 1,
      max: 120,
    }),
    knockoutMinutes: boundedNumber(source.knockoutMinutes, DEFAULT_COTW_CONFIG.knockoutMinutes, {
      min: 1,
      max: 120,
    }),
    interRoundSeconds: boundedNumber(
      source.interRoundSeconds,
      DEFAULT_COTW_CONFIG.interRoundSeconds,
      {
        min: 0,
        max: 86_400,
      }
    ),
    maxProWrSeconds: boundedNumber(source.maxProWrSeconds, DEFAULT_COTW_CONFIG.maxProWrSeconds, {
      min: 1,
      max: 86_400,
    }),
    bufferSeconds: boundedNumber(source.bufferSeconds, DEFAULT_COTW_CONFIG.bufferSeconds, {
      min: 0,
      max: 300,
    }),
    pauseToleranceSeconds: boundedNumber(
      source.pauseToleranceSeconds,
      DEFAULT_COTW_CONFIG.pauseToleranceSeconds,
      { min: 0, max: 60 }
    ),
    finalists: boundedNumber(source.finalists, DEFAULT_COTW_CONFIG.finalists, {
      min: 1,
      max: 100,
      integer: true,
    }),
    activeWindowHours: boundedNumber(
      source.activeWindowHours,
      DEFAULT_COTW_CONFIG.activeWindowHours,
      {
        min: 1,
        max: 168,
      }
    ),
  };
};

/** Read the worker schedule, retaining safe defaults when it is not configured. */
export const getCotwConfig = async () => {
  const snap = await getDoc(doc(db, COTW_CONFIG_COLLECTION, COTW_CONFIG_DOC));
  return snap.exists() ? withConfigDefaults(snap.data()) : withConfigDefaults();
};

/** Subscribe to schedule changes without exposing any worker credentials. */
export const subscribeCotwConfig = (onChange, onError) =>
  onSnapshot(
    doc(db, COTW_CONFIG_COLLECTION, COTW_CONFIG_DOC),
    (snap) => onChange(snap.exists() ? withConfigDefaults(snap.data()) : withConfigDefaults()),
    onError
  );

export const getCotwEvent = async (eventId) => {
  if (!eventId) return null;
  const snap = await getDoc(doc(db, COTW_EVENTS_COLLECTION, eventId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

export const subscribeCotwEvent = (eventId, onChange, onError) => {
  if (!eventId) {
    onChange(null);
    return () => {};
  }
  return onSnapshot(
    doc(db, COTW_EVENTS_COLLECTION, eventId),
    (snap) => onChange(snap.exists() ? { id: snap.id, ...snap.data() } : null),
    onError
  );
};

const listSubcollection = async (eventId, path, sortField, direction = 'asc') => {
  if (!eventId) return [];
  const base = collection(db, COTW_EVENTS_COLLECTION, eventId, ...path);
  try {
    const snap = await getDocs(query(base, orderBy(sortField, direction)));
    return snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
  } catch (error) {
    if (error?.code !== 'failed-precondition') throw error;
    // A fresh deployment may not have the optional composite index yet. Public
    // reads should still work; the worker publishes rank/order fields so this
    // deterministic local fallback is safe.
    const snap = await getDocs(base);
    return snap.docs
      .map((entry) => ({ id: entry.id, ...entry.data() }))
      .sort((a, b) => {
        const left = a[sortField] ?? Number.POSITIVE_INFINITY;
        const right = b[sortField] ?? Number.POSITIVE_INFINITY;
        return (left > right ? 1 : left < right ? -1 : 0) || a.id.localeCompare(b.id);
      });
  }
};

const normalisePageSize = (value, fallback = 50) => {
  const size = Number(value);
  return Number.isInteger(size) && size > 0 ? Math.min(size, 200) : fallback;
};

/**
 * Cursor-based reads for worker projections. `nextCursor` is a Firestore
 * DocumentSnapshot and can be passed back as `cursor`; callers never need to
 * expose document paths or rely on an offset that shifts while the event is
 * live.
 */
const listSubcollectionPage = async (
  eventId,
  path,
  sortField,
  direction = 'asc',
  { pageSize = 50, cursor = null } = {}
) => {
  if (!eventId) return { items: [], nextCursor: null, hasMore: false };
  const base = collection(db, COTW_EVENTS_COLLECTION, eventId, ...path);
  const size = normalisePageSize(pageSize);
  const firestoreSortField = sortField === '__name__' ? documentId() : sortField;
  const constraints = [orderBy(firestoreSortField, direction)];
  if (cursor) constraints.push(startAfter(cursor));
  constraints.push(limit(size));
  try {
    const snap = await getDocs(query(base, ...constraints));
    const items = snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
    return {
      items,
      nextCursor: snap.docs.length === size ? snap.docs[snap.docs.length - 1] : null,
      hasMore: snap.docs.length === size,
    };
  } catch (error) {
    if (error?.code !== 'failed-precondition') throw error;
    // Index rollout can lag the web deploy. Keep the same cursor contract by
    // reading the bounded page and applying the public deterministic order.
    const snap = await getDocs(base);
    const rows = snap.docs
      .map((entry) => ({ snapshot: entry, row: { id: entry.id, ...entry.data() } }))
      .sort((left, right) => {
        const a = sortField === '__name__' ? left.row.id : (left.row[sortField] ?? Infinity);
        const b = sortField === '__name__' ? right.row.id : (right.row[sortField] ?? Infinity);
        const compared = a > b ? 1 : a < b ? -1 : 0;
        return (
          (direction === 'desc' ? -compared : compared) ||
          left.snapshot.id.localeCompare(right.snapshot.id)
        );
      });
    const startIndex = cursor ? rows.findIndex(({ snapshot }) => snapshot.id === cursor.id) + 1 : 0;
    const page = rows.slice(Math.max(0, startIndex), startIndex + size);
    return {
      items: page.map(({ row }) => row),
      nextCursor: page.length === size ? page[page.length - 1].snapshot : null,
      hasMore: page.length === size && startIndex + size < rows.length,
    };
  }
};

export const listCotwParticipants = async (eventId) => {
  if (!eventId) return [];
  const base = collection(db, COTW_EVENTS_COLLECTION, eventId, 'participants');
  const snap = await getDocs(base);
  return snap.docs
    .map((entry) => ({ id: entry.id, ...entry.data() }))
    .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity) || a.id.localeCompare(b.id));
};

export const listCotwParticipantsPage = (eventId, options = {}) =>
  listSubcollectionPage(eventId, ['participants'], '__name__', 'asc', options);

export const listCotwRounds = (eventId) => listSubcollection(eventId, ['rounds'], 'number', 'asc');

export const listCotwRoundsPage = (eventId, options = {}) =>
  listSubcollectionPage(eventId, ['rounds'], 'number', 'asc', options);

export const listCotwRoundResults = (eventId, roundId) =>
  listSubcollection(eventId, ['rounds', roundId, 'results'], 'rank', 'asc');

export const listCotwRoundResultsPage = (eventId, roundId, options = {}) =>
  listSubcollectionPage(eventId, ['rounds', roundId, 'results'], 'rank', 'asc', options);

export const listCotwSubmissions = (eventId) =>
  listSubcollection(eventId, ['submissions'], 'submittedAt', 'asc');

export const listCotwSubmissionsPage = (eventId, options = {}) =>
  listSubcollectionPage(eventId, ['submissions'], 'submittedAt', 'asc', options);

export const subscribeCotwParticipants = (eventId, onChange, onError) => {
  if (!eventId) {
    onChange([]);
    return () => {};
  }
  const base = collection(db, COTW_EVENTS_COLLECTION, eventId, 'participants');
  // Do not orderBy rank: signup rows are intentionally visible before any
  // result exists and an orderBy would exclude documents without that field.
  return onSnapshot(
    base,
    (snap) =>
      onChange(
        snap.docs
          .map((entry) => ({ id: entry.id, ...entry.data() }))
          .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity) || a.id.localeCompare(b.id))
      ),
    async (error) => {
      // Missing optional indexes are common immediately after deploy. Retry a
      // plain collection read rather than making the public board disappear.
      if (error?.code === 'failed-precondition') {
        try {
          const snap = await getDocs(base);
          onChange(
            snap.docs
              .map((entry) => ({ id: entry.id, ...entry.data() }))
              .sort(
                (a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity) || a.id.localeCompare(b.id)
              )
          );
          return;
        } catch (fallbackError) {
          onError?.(fallbackError);
          return;
        }
      }
      onError?.(error);
    }
  );
};

export const subscribeCotwRounds = (eventId, onChange, onError) => {
  if (!eventId) {
    onChange([]);
    return () => {};
  }
  const base = collection(db, COTW_EVENTS_COLLECTION, eventId, 'rounds');
  return onSnapshot(
    query(base, orderBy('number', 'asc')),
    (snap) => onChange(snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }))),
    onError
  );
};

export const subscribeCotwRoundResults = (
  eventId,
  roundId,
  onChange,
  onError,
  { pageSize = 200 } = {}
) => {
  if (!eventId || !roundId) {
    onChange([]);
    return () => {};
  }
  const base = collection(db, COTW_EVENTS_COLLECTION, eventId, 'rounds', roundId, 'results');
  const boundedPageSize = normalisePageSize(pageSize, 200);
  const ordered = query(base, orderBy('rank', 'asc'), limit(boundedPageSize));
  return onSnapshot(
    ordered,
    (snap) =>
      onChange(
        snap.docs
          .map((entry) => ({ id: entry.id, ...entry.data() }))
          .sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity) || a.id.localeCompare(b.id))
      ),
    async (error) => {
      if (error?.code === 'failed-precondition') {
        try {
          const snap = await getDocs(base);
          onChange(
            snap.docs
              .map((entry) => ({ id: entry.id, ...entry.data() }))
              .sort(
                (a, b) =>
                  (a.rank ?? Infinity) - (b.rank ?? Infinity) ||
                  (a.submittedAt ?? Infinity) - (b.submittedAt ?? Infinity) ||
                  a.id.localeCompare(b.id)
              )
              .slice(0, boundedPageSize)
          );
          return;
        } catch (fallbackError) {
          onError?.(fallbackError);
          return;
        }
      }
      onError?.(error);
    }
  );
};

/** Public projection used by the page to select the live/most recent round. */
export const publicCotwRoundId = (event, phase = event?.phase) => {
  if (!event) return null;
  if (event.currentRound?.id) return event.currentRound.id;
  if (phase === COTW_PHASES.QUALIFIER) return 'qualifier';
  return event.lastClosedRoundId || null;
};

/** Current public event id, based solely on the fixed weekly schedule. */
export const currentCotwEventKey = (now = Date.now(), config = DEFAULT_COTW_CONFIG) => {
  return cotwEventKeyFor(now, config);
};

export const nextCotwEvent = (now = Date.now(), config = DEFAULT_COTW_CONFIG) =>
  nextCotwStart(now, {
    timeZone: config.timeZone,
    dayOfWeek: config.dayOfWeek,
    startTime: config.startTime,
  });

const configuredSignupUrl = () =>
  process.env.REACT_APP_COTW_SIGNUP_URL ||
  (process.env.REACT_APP_COTW_WORKER_URL
    ? `${process.env.REACT_APP_COTW_WORKER_URL.replace(/\/$/, '')}/v1/cotw/signup`
    : '');

/**
 * Ask the trusted worker to sign the current user up. The browser sends no
 * Firestore mutation and never supplies a score or replay result.
 */
export const requestCotwSignup = async ({ eventId, steamId, displayName } = {}) => {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Sign in to enter Comp of the Week.');
  if (!eventId) throw new Error('No CotW event is currently available.');
  if (!steamId) throw new Error('Link a canonical CS2KZ/Steam identity first.');
  const endpoint = configuredSignupUrl();
  if (!endpoint) {
    throw new Error('The CotW signup service is not configured yet.');
  }
  const token = await user.getIdToken();
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      eventId,
      steamId,
      displayName: displayName || user.displayName || null,
    }),
  });
  const raw = await response.text();
  let data = {};
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    data = {};
  }
  if (!response.ok) {
    const error = new Error(data.message || raw || `Signup failed (${response.status})`);
    error.code = data.code || data.error || 'COTW_SIGNUP_FAILED';
    error.status = response.status;
    throw error;
  }
  return data;
};

// Semantic aliases for callers that prefer "join" terminology.
export const signupForCotw = requestCotwSignup;
export const joinCotw = requestCotwSignup;
